import { useEffect, useRef, useState } from 'react';
import { Col, Form, Row } from 'antd';
import Input from 'components/new-ui/base/input/Input';
import FiltersModal from 'components/new-ui/composed/modal/filters-modal/FiltersModal';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import useDebounce from 'utils/useDebounce';
import DeclarationStatusSelect from 'views/custom-declaration/declaration-table/components/DeclarationStatusSelect';
import { ArrivalFilterKey, ArrivalFilters } from './useArrivalNotificationsList';

type ModalFilters = Pick<ArrivalFilters, 'status' | 'lrn' | 'relatedMrn'>;

type Props = {
    open: boolean;
    filters: ModalFilters;
    onApply: (key: ArrivalFilterKey, value: string | undefined) => void;
    onClearAll: () => void;
    onClose: () => void;
};

const TEXT_DEBOUNCE_MS = 500;

const ArrivalNotificationsFiltersModal = ({ open, filters, onApply, onClearAll, onClose }: Props) => {
    const [lrn, setLrn] = useState(filters.lrn ?? '');
    const [relatedMrn, setRelatedMrn] = useState(filters.relatedMrn ?? '');

    // Every opening starts from what is applied.
    useEffect(() => {
        if (!open) return;
        setLrn(filters.lrn ?? '');
        setRelatedMrn(filters.relatedMrn ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // `useDebounce` keeps calling the callback it was first given, so the debounced functions read
    // `onApply` through a ref. Otherwise they would apply onto the filters of the first render and
    // drop anything chosen since. One per field, so typing in the second does not cancel the first.
    const onApplyRef = useRef(onApply);
    onApplyRef.current = onApply;

    const applyLrnLater = useDebounce(
        (value: string) => onApplyRef.current('lrn', value.trim() || undefined),
        TEXT_DEBOUNCE_MS
    );
    const applyRelatedMrnLater = useDebounce(
        (value: string) => onApplyRef.current('relatedMrn', value.trim() || undefined),
        TEXT_DEBOUNCE_MS
    );

    const clearAll = () => {
        // A pending text apply would otherwise put its value back after the clear.
        applyLrnLater.cancel();
        applyRelatedMrnLater.cancel();
        setLrn('');
        setRelatedMrn('');
        onClearAll();
    };

    return (
        <FiltersModal
            open={open}
            title="Filter Arrival Notifications"
            onClose={onClose}
            onCancel={onClose}
            onClearFilters={clearAll}
            onSubmit={onClose}
        >
            <Form layout="vertical">
                <Row gutter={40}>
                    <Col span={12}>
                        <Form.Item label="Status">
                            <DeclarationStatusSelect
                                placeholder="Select"
                                value={filters.status}
                                country={DeclarationCountry.EU}
                                internalType={DeclarationInternalType.ARRIVAL_NOTIFICATION_ICS2}
                                onChange={(status?: string) => onApply('status', status)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="LRN">
                            <Input
                                allowClear
                                placeholder="Enter LRN"
                                value={lrn}
                                onChange={(event) => {
                                    setLrn(event.target.value);
                                    applyLrnLater(event.target.value);
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="ENS MRN">
                            <Input
                                allowClear
                                placeholder="Enter the MRN of a referenced ENS"
                                value={relatedMrn}
                                onChange={(event) => {
                                    setRelatedMrn(event.target.value);
                                    applyRelatedMrnLater(event.target.value);
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </FiltersModal>
    );
};

export default ArrivalNotificationsFiltersModal;
